import React, { Component, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, Home } from 'lucide-react';
import { logger } from '@/lib/logger';
import { logAppError } from '@/utils/logAppError';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

function isMongolian(): boolean {
  try {
    const lang = document.documentElement.lang || navigator.language || '';
    return lang.startsWith('mn');
  } catch {
    return false;
  }
}

function txt(mn: string, en: string): string {
  return isMongolian() ? mn : en;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const code = (error as any)?.code;
    if (
      code === 'session_expired' ||
      code === 'refresh_token_not_found' ||
      /Invalid Refresh Token|Session Expired|Auth session missing/i.test(error.message || '')
    ) {
      this.setState({ hasError: false, error: null });
      return;
    }

    // Stale dynamic-import chunk after a deploy — silently reload once.
    if (/Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|error loading dynamically imported module/i.test(error.message || '')) {
      try {
        const last = Number(localStorage.getItem('chunk_reload_at') || '0');
        if (Date.now() - last > 60_000) {
          localStorage.setItem('chunk_reload_at', String(Date.now()));
          window.location.reload();
          return;
        }
      } catch { /* fall through to normal error UI */ }
    }

    logger.error('ErrorBoundary caught error:', {
      error: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    void logAppError({
      source: 'client:ErrorBoundary',
      message: error.message || error.toString(),
      severity: 'error',
      metadata: { stack: error.stack, componentStack: errorInfo.componentStack },
    });

    try {
      const report = {
        error: error.toString(),
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      };
      localStorage.setItem('crash_report', JSON.stringify(report));
    } catch {
      // Ignore localStorage errors
    }

    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error, { extra: errorInfo });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    try {
      const savedSession = localStorage.getItem('current_session_id');
      if (savedSession) {
        window.location.href = '/assistant';
      }
    } catch {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-dvh bg-background p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="flex justify-center">
              <div className="h-20 w-20 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="h-10 w-10 text-destructive" />
              </div>
            </div>

            <div className="space-y-2">
              <h1 className="text-3xl font-bold">
                {txt('Алдаа гарлаа', 'Something went wrong')}
              </h1>
              <p className="text-muted-foreground">
                {txt(
                  'Санаанд байгаа бус алдаа гарлаа. Таны өгөгдөл аюулгүй байна.',
                  "We've encountered an unexpected error. Your data is safe."
                )}
              </p>
              {this.state.error && (
                <details className="text-left mt-4">
                  <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                    {txt('Алдааны дэлгэрэнгүй', 'Error details')}
                  </summary>
                  <pre className="mt-2 p-4 bg-muted rounded text-xs overflow-auto max-h-40">
                    {this.state.error.toString()}
                    {this.state.error.stack && `\n\n${this.state.error.stack}`}
                  </pre>
                </details>
              )}
            </div>

            <div className="flex gap-3 justify-center flex-wrap">
              <Button onClick={this.handleReset}>
                {txt('Дахин оролдох', 'Try Again')}
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/'}
              >
                <Home className="mr-2 h-4 w-4" />
                {txt('Нүүр хуудас', 'Go to Home')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  try {
                    const report = localStorage.getItem('crash_report');
                    if (report) {
                      navigator.clipboard.writeText(report);
                      alert(txt('Алдааны мэдээлэл хуулагдлаа!', 'Crash report copied!'));
                    }
                  } catch {
                    alert(txt('Хуулж чадсангүй', 'Could not copy report'));
                  }
                }}
              >
                {txt('Алдаа хуулах', 'Copy Crash Report')}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
